import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConversations, getConversationMessages, sendDirectMessage, getChatUsers } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  MessageCircle, 
  Send, 
  Search,
  RefreshCw,
  UserPlus,
  Check,
  CheckCheck,
  ArrowLeft,
  Users,
  Smile,
  Paperclip,
  Image,
  Heart,
  ThumbsUp,
  Laugh,
  X,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const ChatPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [messageReactions, setMessageReactions] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const emojis = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

  useEffect(() => {
    fetchConversations();
    fetchAllUsers();
    
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedUser) {
        fetchMessages(selectedUser.user_id);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.user_id);
      setMobileShowChat(true);
    }
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await getChatUsers();
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await getConversationMessages(userId);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectUser = (conv) => {
    setSelectedUser(conv);
    setMessages([]);
  };

  const handleStartNewChat = (chatUser) => {
    setSelectedUser({
      user_id: chatUser.id,
      user_name: chatUser.full_name,
      user_role: chatUser.role
    });
    setShowNewChatDialog(false);
    setUserSearchQuery('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSendingMessage(true);
    try {
      await sendDirectMessage(selectedUser.user_id, newMessage.trim());
      setNewMessage('');
      fetchMessages(selectedUser.user_id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleReaction = (msgId, emoji) => {
    setMessageReactions(prev => {
      const msgReactions = prev[msgId] || {};
      const currentEmoji = msgReactions[user?.id];
      
      if (currentEmoji === emoji) {
        // Remove reaction
        const { [user?.id]: _, ...rest } = msgReactions;
        return { ...prev, [msgId]: rest };
      } else {
        // Add/change reaction
        return { ...prev, [msgId]: { ...msgReactions, [user?.id]: emoji } };
      }
    });
    setShowEmojiPicker(null);
    toast.success('Reaction added!');
  };

  const handleFileUpload = () => {
    toast.info('File sharing coming soon!');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500';
      case 'supervisor': return 'bg-purple-500';
      case 'crew_leader': return 'bg-green-600';
      case 'crew_member': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const getRoleBadge = (role) => {
    const labels = {
      'admin': 'Admin',
      'supervisor': 'Super',
      'crew_leader': 'Lead',
      'crew_member': 'Crew'
    };
    return labels[role] || role;
  };

  const filteredConversations = conversations.filter(c =>
    c.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => 
    u.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toLocaleDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  const getReactionCounts = (msgId) => {
    const reactions = messageReactions[msgId] || {};
    const counts = {};
    Object.values(reactions).forEach(emoji => {
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return counts;
  };

  return (
    <div className="h-[calc(100vh-80px)] flex overflow-hidden" data-testid="chat-page">
      {/* Left Sidebar */}
      <div className={`w-full md:w-72 lg:w-80 flex flex-col bg-gradient-to-b from-green-50 to-emerald-50 border-r border-green-200 overflow-hidden ${mobileShowChat && selectedUser ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-3 border-b border-green-200 bg-white/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-green-800">Team Chat</h1>
            </div>
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0"
              onClick={() => setShowNewChatDialog(true)}
              data-testid="new-chat-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 bg-white/80 border-green-200 text-sm"
              data-testid="search-conversations"
            />
          </div>
        </div>

        {/* Conversations List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-5 h-5 animate-spin text-green-500" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-6 h-6 text-green-500" />
              </div>
              <p className="font-medium text-green-800 text-sm">No chats yet</p>
              <p className="text-xs text-green-600 mt-1">Start a conversation</p>
              <Button 
                className="mt-3 bg-green-600 hover:bg-green-700 h-8 text-xs" 
                size="sm"
                onClick={() => setShowNewChatDialog(true)}
              >
                <UserPlus className="w-3 h-3 mr-1" />
                New Chat
              </Button>
            </div>
          ) : (
            <div className="py-1">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.user_id}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors overflow-hidden ${
                    selectedUser?.user_id === conv.user_id 
                      ? 'bg-green-200/70 border-l-3 border-l-green-600' 
                      : 'hover:bg-green-100/50'
                  }`}
                  onClick={() => handleSelectUser(conv)}
                  data-testid={`conv-${conv.user_id}`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className={`${getRoleColor(conv.user_role)} text-white text-sm`}>
                        {conv.user_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="font-semibold text-sm text-green-900 truncate">{conv.user_name}</p>
                      <span className="text-[10px] text-green-600 flex-shrink-0">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-green-700 truncate">{conv.last_message || 'No messages'}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] text-white font-bold">{conv.unread_count}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-gradient-to-br from-slate-50 to-green-50/30 ${!mobileShowChat || !selectedUser ? 'hidden md:flex' : 'flex'}`}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div className="px-4 py-2.5 border-b border-green-200 bg-white/80 backdrop-blur flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="md:hidden h-8 w-8 p-0"
                  onClick={() => setMobileShowChat(false)}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Avatar className="h-9 w-9">
                  <AvatarFallback className={`${getRoleColor(selectedUser.user_role)} text-white text-sm`}>
                    {selectedUser.user_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-green-900 text-sm">{selectedUser.user_name}</p>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span className="text-[10px] text-green-600">Online</span>
                    <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 h-4">
                      {getRoleBadge(selectedUser.user_role)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="font-medium text-green-800">Start chatting</p>
                    <p className="text-sm text-green-600 mt-1">Send a message to {selectedUser.user_name?.split(' ')[0]}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedMessages).map(([date, dayMessages]) => (
                    <div key={date}>
                      <div className="flex items-center justify-center mb-3">
                        <span className="px-2.5 py-1 bg-white rounded-full text-[10px] text-green-700 shadow-sm border border-green-100">
                          {new Date(date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {dayMessages.map((msg, idx) => {
                          const isMe = msg.sender_id === user?.id;
                          const showAvatar = idx === 0 || dayMessages[idx - 1]?.sender_id !== msg.sender_id;
                          const reactionCounts = getReactionCounts(msg.id);
                          
                          return (
                            <div
                              key={msg.id}
                              className={`flex items-end gap-1.5 ${isMe ? 'justify-end' : 'justify-start'}`}
                              data-testid={`msg-${msg.id}`}
                            >
                              {!isMe && showAvatar && (
                                <Avatar className="h-7 w-7 flex-shrink-0">
                                  <AvatarFallback className={`${getRoleColor(selectedUser.user_role)} text-white text-xs`}>
                                    {selectedUser.user_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              {!isMe && !showAvatar && <div className="w-7" />}
                              <div className={`max-w-[75%] group relative ${isMe ? 'order-1' : 'order-2'}`}>
                                <div
                                  className={`px-3 py-2 rounded-2xl ${
                                    isMe 
                                      ? 'bg-green-600 text-white rounded-br-md' 
                                      : 'bg-white text-green-900 rounded-bl-md shadow-sm border border-green-100'
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                                </div>
                                
                                {/* Reactions Display */}
                                {Object.keys(reactionCounts).length > 0 && (
                                  <div className={`flex gap-0.5 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    {Object.entries(reactionCounts).map(([emoji, count]) => (
                                      <span key={emoji} className="text-xs bg-white rounded-full px-1.5 py-0.5 shadow-sm border">
                                        {emoji} {count > 1 && count}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Time and Status */}
                                <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                  <span className="text-[10px] text-green-600">{formatMessageTime(msg.created_at)}</span>
                                  {isMe && (
                                    msg.read 
                                      ? <CheckCheck className="w-3 h-3 text-green-600" />
                                      : <Check className="w-3 h-3 text-green-400" />
                                  )}
                                </div>

                                {/* Reaction Button */}
                                <Popover open={showEmojiPicker === msg.id} onOpenChange={(open) => setShowEmojiPicker(open ? msg.id : null)}>
                                  <PopoverTrigger asChild>
                                    <button 
                                      className={`absolute ${isMe ? '-left-6' : '-right-6'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 bg-white rounded-full shadow flex items-center justify-center hover:bg-green-50`}
                                    >
                                      <Smile className="w-3 h-3 text-green-600" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-1.5" align={isMe ? 'start' : 'end'}>
                                    <div className="flex gap-1">
                                      {emojis.map(emoji => (
                                        <button
                                          key={emoji}
                                          onClick={() => handleReaction(msg.id, emoji)}
                                          className="w-7 h-7 hover:bg-green-100 rounded flex items-center justify-center text-base transition-colors"
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="p-3 border-t border-green-200 bg-white/80 backdrop-blur">
              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  className="text-green-600 hover:text-green-700 hover:bg-green-100 h-8 w-8 p-0"
                  onClick={handleFileUpload}
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  className="text-green-600 hover:text-green-700 hover:bg-green-100 h-8 w-8 p-0"
                  onClick={handleFileUpload}
                >
                  <Image className="w-4 h-4" />
                </Button>
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 bg-green-50 border-green-200 focus:border-green-400 h-9 text-sm"
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 h-9 w-9 p-0 rounded-full"
                  disabled={sendingMessage || !newMessage.trim()}
                  data-testid="send-chat-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-lg font-bold text-green-800">Team Chat</h2>
              <p className="text-green-600 mt-1 text-sm max-w-xs">
                Select a chat or start a new conversation
              </p>
              <Button 
                className="mt-4 bg-green-600 hover:bg-green-700"
                onClick={() => setShowNewChatDialog(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                New Chat
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="max-w-sm bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 border-green-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-800">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-white" />
              </div>
              New Chat
            </DialogTitle>
            <DialogDescription className="text-green-700">
              Select a team member to start chatting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              <Input
                placeholder="Search team..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="pl-8 bg-white/80 border-green-200 h-9 text-sm"
              />
            </div>
            <ScrollArea className="h-64">
              <div className="space-y-1">
                {filteredUsers.map((chatUser) => (
                  <div
                    key={chatUser.id}
                    className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-green-100 cursor-pointer transition-colors"
                    onClick={() => handleStartNewChat(chatUser)}
                    data-testid={`new-chat-user-${chatUser.id}`}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className={`${getRoleColor(chatUser.role)} text-white text-sm`}>
                        {chatUser.full_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-green-900 truncate">{chatUser.full_name}</p>
                      <p className="text-[10px] text-green-600 truncate">{getRoleBadge(chatUser.role)}</p>
                    </div>
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="text-center py-6 text-green-600 text-sm">
                    No users found
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
